// import { Promo } from "../components/sections/Promo";
import { useState } from "react";
import Navbar from "../components/layout/Navbar";
import style from "../styles/Home.module.scss";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Pagination, Navigation } from "swiper/modules";
import Button from "../../ui/Button";
import paths from "../../lib/paths";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import { PurposeCtaArea } from "../components/sections/PurposeCtaArea";
import { RedefineSignUp } from "../components/sections/RedefineSignUp";
import Footer from "../components/layout/Footer";
import { useNavigate } from "react-router-dom";
import ContactUsModal from "../components/layout/ContactUsModal";
import { TabComponent } from "../components/layout/Tabs";
import PlacesInput from "../../app/Dashboard/components/PlacesInput";
import { useAppDispatch, useIsAuthenticated } from "../../store/hooks";
import { journeyUpdated } from "../../store/slices/rideSearchSlice";

/**
 * The public landing page.
 *
 * It no longer intercepts `?mode=verifyEmail` and `?mode=resetPassword`. Those
 * were Firebase action-code links, and the API sends a numeric code by email
 * rather than a link, so the branches could never fire and the one that read
 * `auth.currentUser` referenced a service this app no longer talks to.
 */
function Home() {
    const [openModal, setOpenModal] = useState(false);

    return (
        <>
            {openModal && <ContactUsModal setOpenModal={setOpenModal} />}
            <Navbar />
            <HomeHeroSlider />
            <FeatureArea />
            {/* <Promo /> */}
            {/* <HitchRide /> */}
            <PurposeCtaArea />
            <Cities />
            <RedefineSignUp />
            <Footer setOpenModal={setOpenModal} />
        </>
    );
}

export default Home;

// !! =========== !! HERO SLIDER AREA COMPONENT ?? //
export function HomeHeroSlider() {
    // Starts false because RIDE is the tab that starts selected below.
    const [driverTab, setDriverTab] = useState(false);
    const navigate = useNavigate();

    // RIDE first and selected on arrival, as the design has it. Landing on
    // DRIVE showed a visitor a single sentence and a button, where the ride
    // search is the thing the hero is built around.
    const tabs = [
        {
            label: "RIDE",
            onClick: () => setDriverTab(false),
            content: <RideTab />,
        },
        {
            label: "DRIVE",
            onClick: () => setDriverTab(true),
            content: (
                <>
                    <div className={style.adText}>
                        <h4>Choose to become a driver on Hytch Buddy today!</h4>
                    </div>
                    <div className={style.buttonBox}>
                        <Button
                            variant="primary"
                            content="Get Started"
                            onClick={() => navigate("/dashboard/driver/set-up")}
                        />
                    </div>
                </>
            ),
        },
    ];

    return (
        <div className={style.homeContainer}>
            <div className={style.overlay}>
                <div className={style.contentBox}>
                    {driverTab ? (
                        <div className={style.heroAdCopy}>
                            <h3>Need a lift, we’ve got you covered.</h3>
                            <p>
                                Whether you're a driver with extra space or a
                                rider seeking a convenient way to journey, we've
                                got you covered for medium to long distance
                                rides.
                            </p>
                        </div>
                    ) : (
                        <div className={style.heroAdCopy}>
                            <h3>Need a lift, we’ve got you covered.</h3>
                            <p>
                                Whether you're a driver with extra space or a
                                rider seeking a convenient way to journey, we've
                                got you covered for medium to long distance
                                rides.
                            </p>
                        </div>
                    )}
                    {/* //?? ==== GLASS TRAVEL TABS COMPONENT ===== ??//*/}
                    <TabComponent tabs={tabs} />
                </div>
            </div>
            {/* //?? ======= HERO SLIDE SWIPER ===== ??// */}
            <Swiper
                className={style.homeHeroSlider}
                autoplay={{
                    delay: 5000,
                    disableOnInteraction: false,
                }}
                // pagination={{
                //     clickable: true,
                // }}
                pagination={false}
                modules={[Autoplay, Pagination, Navigation]}
            >
                <SwiperSlide className={style.bgCoupleCarTrip}></SwiperSlide>
                {/* <SwiperSlide className={style.bgCoupleCarTrip}></SwiperSlide>
                <SwiperSlide className={style.bgCoupleCarTrip}></SwiperSlide> */}
            </Swiper>
        </div>
    );
}

/**
 * The hero's ride search: two addresses and nothing else.
 *
 * There is no map here. The landing page is the entry point, so all it collects
 * is where the journey starts and ends; the route, the time, the seats and the
 * rest of the detail belong to the dashboard, which is the screen built to ask
 * for them. Drawing the route twice also meant a visitor who never signed in
 * still cost us a directions request per address they tried.
 */
const RideTab = () => {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const signedIn = useIsAuthenticated();

    const [startLoc, setStartLoc] = useState({
        name: "",
        address: "",
        latitude: 0,
        longitude: 0,
    });

    const [endLoc, setEndLoc] = useState({
        name: "",
        address: "",
        latitude: 0,
        longitude: 0,
    });

    /**
     * Carries the journey into the dashboard rather than searching from here.
     *
     * Only the two addresses: this form has no date, time or seat count to
     * offer, and dispatching a departure time built from the clock would put a
     * stale one into the dashboard's form for the rider to notice and correct.
     *
     * Someone signed out is sent to sign in first, with the dashboard recorded
     * as where they were going, which is the same shape PrivateRoute uses. The
     * draft lives in the store and survives that redirect, so the search box on
     * the other side is filled in whichever way they arrive.
     */
    const bookARide = () => {
        if (!startLoc.address || !endLoc.address) return;

        dispatch(
            journeyUpdated({
                startAddress: startLoc.address,
                endAddress: endLoc.address,
                origin: {
                    longitude: startLoc.longitude,
                    latitude: startLoc.latitude,
                },
                destination: {
                    longitude: endLoc.longitude,
                    latitude: endLoc.latitude,
                },
            })
        );

        const destination = "/dashboard/rider/overview";
        if (!signedIn) {
            void navigate("/login", {
                state: { from: { pathname: destination } },
            });
            return;
        }
        void navigate(destination);
    };

    return (
        <div>
            <div className={style.rideTab}>
                <div className={style.inputArea}>
                    {/* `htmlFor` pointed at "pickUpLocation", which is not
                        the id of this input or of any other element on the
                        page, so neither label was associated with a field:
                        clicking one focused nothing and a screen reader
                        announced both inputs unlabelled. */}
                    <label htmlFor="startLoc">Pick up location</label>
                    <div className={style.inputCover}>
                        <PlacesInput
                            className="primary"
                            id="startLoc"
                            setter={setStartLoc}
                            name="startLoc"
                            placeholder="Where do we pick you?"
                        />
                    </div>
                </div>
                <div className={style.inputArea}>
                    <label htmlFor="endLoc">Destination</label>
                    <div className={style.inputCover}>
                        <PlacesInput
                            className="primary"
                            id="endLoc"
                            setter={setEndLoc}
                            name="endLoc"
                            placeholder="Where are you going?"
                            offerCurrentLocation={false}
                        />
                    </div>
                </div>
                <div className={style.buttonBox}>
                    <Button
                        variant="primary"
                        content="Book a ride"
                        onClick={bookARide}
                    />
                </div>
            </div>
        </div>
    );
};

//!! ============== FEATURES IMAGES COMPONENT ?? //
export function FeatureArea() {
    const navigate = useNavigate();
    return (
        <div className={style.featureArea}>
            <div className="wrapper-constrain">
                <div className={style.featureOne}>
                    <div>
                        <img
                            src={`${paths.images}/girl-catching-car.png`}
                            alt="girl catching a car"
                        />
                    </div>
                    <div className="">
                        <h3>Hitch your way to safe travels</h3>
                        <p>
                            Discover verified drivers heading your way and join
                            their ride for a reasonable fee. Travel comfortably
                            and conveniently while saving both time and money.
                        </p>
                        <Button
                            onClick={() =>
                                void navigate("/dashboard/rider/overview")
                            }
                            variant="primary"
                            content="Get a ride"
                        />
                        <Button
                            onClick={() => navigate("/ride")}
                            variant="secondary"
                            content="Learn More >"
                        />
                    </div>
                </div>
                <div className={style.featureTwo}>
                    <div className="">
                        <div className={style.featureTwoHeading}>
                            <p>WHY HYTCH BUDDY</p>
                            <h3>Explore the future of travels</h3>
                        </div>
                        <div className={style.grid_col_2}>
                            <div className="">
                                <img
                                    src={`${paths.icons}/feature1.png`}
                                    alt="girl catching a car"
                                />
                                <h4>Ease of Use</h4>
                                <p>
                                    We connect drivers and riders seamlessly. A
                                    few clicks is all it takes to find or offer
                                    your ideal ride.
                                </p>
                            </div>
                            <div className="">
                                <img
                                    src={`${paths.icons}/feature2.png`}
                                    alt="girl catching a car"
                                />
                                <h4>Budget-friendly</h4>
                                <p>
                                    Save money while connecting with like-minded
                                    travelers. We offer an affordable
                                    alternative to traditional travel methods.
                                </p>
                            </div>
                            <div className="">
                                <img
                                    src={`${paths.icons}/feature3.png`}
                                    alt="girl catching a car"
                                />
                                <h4>Verified Drivers</h4>
                                <p>
                                    Your safety and comfort is our priority. We
                                    verify all drivers based on experience and
                                    professionalism before approval.
                                </p>
                            </div>
                            <div className="">
                                <img
                                    src={`${paths.icons}/feature4.png`}
                                    alt="girl catching a car"
                                />
                                <h4>Eco-Friendly</h4>
                                <p>
                                    We're committed to a greener future. Join us
                                    in reducing our carbon footprint one ride at
                                    a time.
                                </p>
                            </div>
                        </div>
                    </div>
                    <div className={style.featureTwoImages}>
                        <div className="">
                            <div className={style.miniBox}>
                                <img
                                    src={`${paths.icons}/blue-circle-check.png`}
                                    alt="blue-circle-check.png"
                                />
                                <div className="">
                                    <p>Accessible</p>
                                    <span></span>
                                </div>
                            </div>
                            <img
                                src={`${paths.images}/successful-woman-with-keys.png`}
                                alt=""
                            />
                        </div>
                        <div className="">
                            <div className={style.miniBox2}>
                                <img
                                    src={`${paths.icons}/blue-circle-check.png`}
                                    alt="blue-circle-check.png"
                                />
                                <div className="">
                                    <p>Reliable</p>
                                    <span></span>
                                </div>
                            </div>
                            <img
                                src={`${paths.images}/taxi-driver-ready.png`}
                                alt=""
                            />
                        </div>
                    </div>
                    <div className={style.featureTwoImagesMobile}>
                        <img
                            src={`${paths.images}/merged-homepage-image.png`}
                            alt=""
                        />
                    </div>
                </div>
                <div className={style.featureThree}>
                    <div className="">
                        <img
                            src={`${paths.images}/stylish-black-man-sitting-wheel-luxury.png`}
                            alt=""
                        />
                    </div>
                    <div className="">
                        <h3>Connect and earn as you go</h3>
                        <p>
                            We match you with paying riders who are heading your
                            direction, allowing you to effortlessly cover your
                            costs and make your journey more enjoyable.
                        </p>
                        <Button
                            onClick={() => navigate("/dashboard/driver/set-up")}
                            variant="primary"
                            content="Apply to drive"
                        />
                        <Button
                            onClick={() => navigate("/drive")}
                            variant="secondary"
                            content="Learn More >"
                        />
                    </div>
                </div>
            </div>
        </div>
    );
}

//!! ============== HITCH A RIDE COMPONENT ?? //
export function HitchRide() {
    const navigate = useNavigate();
    return (
        <>
            <div className={style.featureArea}>
                <div className="wrapper-constrain">
                    <div className={style.featureOne}>
                        <div>
                            <img
                                src={`${paths.images}/girl-catching-car.png`}
                                alt="girl catching a car"
                            />
                        </div>
                        <div className="">
                            <h3>Hitch your way to safe travels</h3>
                            <p>
                                Discover verified drivers heading your way and
                                join their ride for a reasonable fee. Travel
                                comfortably and conveniently while saving both
                                time and money.
                            </p>
                            <Button
                                onClick={() =>
                                    void navigate("/dashboard/rider/overview")
                                }
                                variant="primary"
                                content="Get a ride"
                            />
                            <Button
                                onClick={() => navigate("/ride")}
                                variant="secondary"
                                content="Learn More >"
                            />
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}

//!! ============== AVAILABLE CITIES COMPONENT ?? //
export function Cities() {
    const navigate = useNavigate();
    return (
        <>
            <div className={style.CitiesArea}>
                <div className="wrapper-constrain">
                    <div className={style.CitiesFeature}>
                        <div className={style.CitiesFeatureImageBox}>
                            <p className={style.WordBlue}>
                                WHERE CAN YOU JOIN A RIDE WITH HYTCH BUDDY
                            </p>
                            <h2>See our current Campus Cities</h2>
                            <br />
                            <img
                                src={`${paths.images}/citiesImages.png`}
                                alt="City Map"
                            />
                        </div>
                        <div className={style.partTwo}>
                            <p className={style.wordSmall}>
                                We offer an affordable fare price for your mid
                                and long distance trips
                            </p>
                            <div className={style.pickUpDestinationLocation}>
                                <div>
                                    <img
                                        src={`${paths.svgs}/destination-outline-blue.svg`}
                                        alt="pick up location"
                                    />
                                    <div className="">
                                        <h3>Georgia</h3>
                                        {/* <p>Description text here </p> */}
                                    </div>
                                </div>
                                <br />
                                <br />
                                <div className="">
                                    <img
                                        src={`${paths.svgs}/destination-outline-blue.svg`}
                                        alt="destination location"
                                    />
                                    <div className="">
                                        <h3>Illinois</h3>
                                        {/* <p>Description text here </p> */}
                                    </div>
                                </div>
                            </div>
                            <p className={style.moreCampus}>
                                More campus cities coming soon
                            </p>

                            <Button
                                onClick={() =>
                                    void navigate("/dashboard/rider/overview")
                                }
                                variant="primary"
                                content="Get a ride"
                            />
                            <Button
                                onClick={() => navigate("/ride")}
                                variant="secondary"
                                content="Learn More >"
                            />
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}
